import { create } from 'zustand';
import type { Adventure } from '@gm-binder/shared';
import { api } from '../api/client';

/**
 * Authoring store for the adventure editor. Holds a working copy of the adventure and
 * autosaves it (debounced, whole-object PUT) so editing stays local-first and responsive.
 *
 * A revision counter guards the in-flight save: the canonical server response is only adopted
 * when no further edits happened while saving, so an active edit is never clobbered.
 */
interface EditorStore {
  slug: string | null;
  adventure: Adventure | null;
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  error: string | null;
  lastSavedAt: number | null;

  load: (slug: string) => Promise<void>;
  reset: () => void;
  /** Apply a mutation to the working copy and schedule an autosave. */
  update: (mutator: (adv: Adventure) => void) => void;
  /** Flush any pending save immediately (e.g. before navigating away). */
  saveNow: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let rev = 0;
const DEBOUNCE_MS = 800;

async function flush(get: () => EditorStore, set: (p: Partial<EditorStore>) => void) {
  const { slug, adventure } = get();
  if (!slug || !adventure) return;
  const savedRev = rev;
  set({ saving: true, error: null });
  try {
    const canonical = await api.updateAdventure(slug, adventure);
    // Only adopt the canonical (re-normalized) copy if nothing changed meanwhile.
    if (rev === savedRev) {
      set({ adventure: canonical, saving: false, dirty: false, lastSavedAt: Date.now() });
    } else {
      set({ saving: false, lastSavedAt: Date.now() });
    }
  } catch (e) {
    set({ saving: false, error: e instanceof Error ? e.message : 'Salvataggio fallito' });
  }
}

function scheduleSave(get: () => EditorStore, set: (p: Partial<EditorStore>) => void) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void flush(get, set), DEBOUNCE_MS);
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  slug: null,
  adventure: null,
  loading: false,
  saving: false,
  dirty: false,
  error: null,
  lastSavedAt: null,

  async load(slug) {
    set({ loading: true, error: null, slug });
    try {
      const adventure = await api.getAdventure(slug);
      set({ adventure, loading: false, dirty: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Caricamento fallito' });
    }
  },

  reset() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    set({ slug: null, adventure: null, error: null, dirty: false, saving: false });
  },

  update(mutator) {
    const { adventure } = get();
    if (!adventure) return;
    const next: Adventure = structuredClone(adventure);
    mutator(next);
    rev += 1;
    set({ adventure: next, dirty: true });
    scheduleSave(get, set);
  },

  async saveNow() {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    await flush(get, set);
  },
}));
